/**
 * LeaveBalanceCard Component
 * Displays a leave type balance with progress bar
 */

import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils/cn";

type ColorVariant = "info" | "success" | "warning" | "error";

interface LeaveBalanceCardProps {
  /** Card title (e.g., "Congés Payés") */
  title: string;
  /** Days already used */
  used: number;
  /** Total days allocated */
  total: number;
  /** Progress bar color variant */
  color?: ColorVariant;
  /** Reference year (e.g., 2025) */
  year?: number;
  /** Unit label for "restants" and "utilisé(s)" (default: "jours") */
  unit?: string;
  /** Optional formatter for the displayed values (e.g. minutes -> "15h 56min") */
  formatValue?: (value: number) => string;
  /** Additional CSS classes */
  className?: string;
  /** Inline styles for animation delays */
  style?: React.CSSProperties;
}

/**
 * Leave balance card with DaisyUI progress bar
 * Shows used/total days with visual progress indicator
 * Enhanced with hover animations, gradients, and glow effects
 */
export function LeaveBalanceCard({
  title,
  used,
  total,
  color = "info",
  year,
  unit = "jours",
  formatValue,
  className = "",
  style,
}: LeaveBalanceCardProps) {
  const display = formatValue ?? ((v: number) => v);
  // Calculate percentage, handling edge cases
  const isOverflow = used > total;
  const percentage = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const overflowPercentage = isOverflow ? ((used - total) / total) * 100 : 0;
  const balance = Math.max(0, total - used);

  // Color variants mapping
  const colorMap: Record<
    ColorVariant,
    { bar: string; text: string; bg: string; gradient: string; glow: string; border: string }
  > = {
    info: {
      bar: "progress-info",
      text: "text-info",
      bg: "bg-info",
      gradient: "bg-gradient-to-br from-info/5 to-info/10",
      glow: "hover:shadow-info/20 hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-info)_30%,transparent)]",
      border: "border-info/30",
    },
    success: {
      bar: "progress-success",
      text: "text-success",
      bg: "bg-success",
      gradient: "bg-gradient-to-br from-success/5 to-success/10",
      glow: "hover:shadow-success/20 hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-success)_30%,transparent)]",
      border: "border-success/30",
    },
    warning: {
      bar: "progress-warning",
      text: "text-warning",
      bg: "bg-warning",
      gradient: "bg-gradient-to-br from-warning/5 to-warning/10",
      glow: "hover:shadow-warning/20 hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-warning)_30%,transparent)]",
      border: "border-warning/30",
    },
    error: {
      bar: "progress-error",
      text: "text-error",
      bg: "bg-error",
      gradient: "bg-gradient-to-br from-error/5 to-error/10",
      glow: "hover:shadow-error/20 hover:shadow-[0_8px_32px_-8px_color-mix(in_oklab,var(--color-error)_30%,transparent)]",
      border: "border-error/30",
    },
  };

  const colors = colorMap[color];

  return (
    <div
      className={cn(
        "card bg-base-200 border border-base-300/60 rounded-xl overflow-hidden",
        "transition-all duration-300 ease-out",
        "hover:scale-[1.02]",
        colors.glow,
        className,
      )}
      style={style}
    >
      {/* Gradient overlay on hover */}
      <div
        className={cn(
          "absolute inset-0 opacity-0 transition-opacity duration-300",
          colors.gradient,
          "hover:opacity-100",
        )}
      />

      <div className="card-body p-5 relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 group/header">
          <div>
            <h4 className="card-title text-sm font-semibold text-base-content/80 mb-0.5">
              {title}
            </h4>
            {year && <p className="text-xs text-base-content/50">Année {year}</p>}
          </div>
          {/* Chevron indicator */}
          <div
            className={cn(
              "w-6 h-6 rounded-lg flex items-center justify-center",
              "bg-base-200/50 text-base-content/40",
              "transition-all duration-300",
              "group-hover/header:translate-x-1 group-hover/header:text-base-content/60",
            )}
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Balance Value */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className={cn("text-3xl font-bold tracking-tight", colors.text)}>
            {display(balance)}
          </span>
          <span className="text-sm text-base-content/60">
            {formatValue ? "" : `${unit} `}
            restant{unit === "heures" ? "es" : balance === 1 ? "" : "s"}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          {/* Main progress */}
          <div className="w-full bg-base-200 rounded-full h-2.5 overflow-hidden">
            <div
              className={cn("h-full rounded-full transition-all duration-500", colors.bar)}
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          {/* Overflow indicator */}
          {isOverflow && (
            <div className={cn("flex items-center gap-2 text-xs font-medium", "text-error")}>
              <span className="w-1.5 h-1.5 rounded-full bg-error" />
              <span>
                +{display(used - total)}
                {formatValue ? "" : ` ${unit}`} au-delà du solde
              </span>
            </div>
          )}

          {/* Stats row */}
          <div className="flex justify-between text-xs text-base-content/50 pt-1">
            <span>
              {display(used)} {formatValue ? "" : `${unit} `}
              utilisé
              {unit === "heures" ? (used !== 1 ? "es" : "e") : used !== 1 ? "s" : ""}
            </span>
            <span>{display(total)} total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact leave balance badge for inline display
 */
export function LeaveBalanceBadge({
  title,
  remaining,
  color = "info",
}: {
  title: string;
  remaining: number;
  color?: ColorVariant;
}) {
  const colorMap: Record<ColorVariant, string> = {
    info: "badge-info",
    success: "badge-success",
    warning: "badge-warning",
    error: "badge-error",
  };

  return (
    <div className="badge badge-lg gap-2 px-4 py-3 shadow-sm">
      <span className="text-base-content/60 font-medium">{title}:</span>
      <span className={cn("font-bold", colorMap[color])}>{remaining}j</span>
    </div>
  );
}
